import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Pause, Play, Search } from 'lucide-react'
import '@fontsource-variable/inter'
import './history.css'
import LeagueChart from './LeagueChart'
import LeagueOverview from './LeagueOverview'
import Standings from './Standings'
import TeamStory from './TeamStory'
import { RANGES, rangeDates, useLeagueHistory } from './data'
import { TeamLogo, formatLongDay } from './ui'

const METRICS = [
  { id: 'rank', label: 'Rank' },
  { id: 'score', label: 'Score' },
]

function readUrl() {
  const params = new URLSearchParams(window.location.search)
  return {
    team: params.get('team'),
    date: params.get('date'),
    range: RANGES.some(r => r.id === params.get('range')) ? params.get('range') : '90',
    metric: params.get('metric') === 'score' ? 'score' : 'rank',
  }
}

function Segmented({ options, value, onChange, label }) {
  return (
    <div className="hv-segmented" role="radiogroup" aria-label={label}>
      {options.map(o => (
        <button key={o.id} role="radio" aria-checked={value === o.id} className={value === o.id ? 'is-active' : ''} onClick={() => onChange(o.id)}>
          {value === o.id && <motion.span layoutId={`seg-${label}`} className="hv-segmented-thumb" transition={{ type: 'spring', stiffness: 500, damping: 38 }} />}
          <span className="hv-segmented-label">{o.label}</span>
        </button>
      ))}
    </div>
  )
}

function TeamPicker({ league, value, onChange }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef(null)
  const team = value ? league.byId.get(value) : null

  useEffect(() => {
    if (!open) return
    const close = e => !ref.current?.contains(e.target) && setOpen(false)
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [open])

  const q = query.trim().toLowerCase()
  const matches = league.teams.filter(t => !q || t.name.toLowerCase().includes(q) || t.acronym.toLowerCase().includes(q))

  return (
    <div className="hv-picker" ref={ref}>
      <button className="hv-picker-btn" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {team ? <TeamLogo team={team} size={18} /> : <Search size={15} />}
        <span>{team ? team.name : 'Find a team'}</span>
        <ChevronDown size={14} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="hv-picker-pop"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
          >
            <input
              autoFocus
              placeholder="Search teams…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && matches[0]) {
                  onChange(matches[0].id)
                  setOpen(false)
                  setQuery('')
                }
                if (e.key === 'Escape') setOpen(false)
              }}
            />
            <div className="hv-picker-grid">
              {matches.map(t => (
                <button
                  key={t.id}
                  className={t.id === value ? 'is-active' : ''}
                  onClick={() => {
                    onChange(t.id)
                    setOpen(false)
                    setQuery('')
                  }}
                >
                  <TeamLogo team={t} size={28} />
                  <span>{t.acronym}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function HistoryView() {
  const { league, error, loading } = useLeagueHistory()
  const initial = useMemo(readUrl, [])
  const [range, setRange] = useState(initial.range)
  const [metric, setMetric] = useState(initial.metric)
  // Focus is kept as the team acronym so ?team= resolves on the first render.
  const [focusAcronym, setFocusAcronym] = useState(initial.team?.toUpperCase() || null)
  const [pinnedDate, setPinnedDate] = useState(initial.date)
  const [hoverId, setHoverId] = useState(null)
  const [playing, setPlaying] = useState(false)

  const dates = useMemo(() => (league ? rangeDates(league, range) : []), [league, range])
  const focus = (focusAcronym && league?.byAcronym.get(focusAcronym)) || null
  const focusId = focus?.id || null
  const setFocusId = id => setFocusAcronym(id ? league.byId.get(id)?.acronym || null : null)
  // A pinned date (possibly from the URL) snaps to the snapshot in effect that day.
  const pinned = useMemo(() => {
    if (!pinnedDate || !dates.length || pinnedDate < dates[0]) return null
    return dates.findLast(d => d <= pinnedDate)
  }, [pinnedDate, dates])
  const activeDate = pinned || dates.at(-1)

  // Keep the URL shareable without adding history entries on every click.
  useEffect(() => {
    if (!league) return
    const params = new URLSearchParams()
    if (focus) params.set('team', focus.acronym)
    if (pinnedDate) params.set('date', pinnedDate)
    if (range !== '90') params.set('range', range)
    if (metric !== 'rank') params.set('metric', metric)
    const search = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}`)
  }, [league, focus, pinnedDate, range, metric])

  // Replay the period: walk the pinned date forward through every snapshot.
  useEffect(() => {
    if (!playing) return
    const id = setInterval(() => {
      setPinnedDate(current => {
        const i = current ? dates.indexOf(current) : -1
        if (i >= dates.length - 1) {
          setPlaying(false)
          return current
        }
        return dates[i + 1]
      })
    }, 420)
    return () => clearInterval(id)
  }, [playing, dates])

  const togglePlay = () => {
    if (!playing && (!pinnedDate || pinnedDate === dates.at(-1) || !dates.includes(pinnedDate))) setPinnedDate(dates[0])
    setPlaying(p => !p)
  }

  const step = useCallback(
    dir => {
      const i = dates.indexOf(activeDate)
      const next = dates[Math.max(0, Math.min(dates.length - 1, i + dir))]
      if (next) setPinnedDate(next)
    },
    [dates, activeDate]
  )

  useEffect(() => {
    const onKey = e => {
      if (e.target.closest('input, textarea')) return
      if (e.key === 'ArrowLeft') step(-1)
      else if (e.key === 'ArrowRight') step(1)
      else if (e.key === 'Escape') setFocusId(null)
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [step])

  const selectTeam = id => setFocusId(id)

  const pick = ({ teamId, date }) => {
    if (teamId) setFocusId(teamId)
    if (date) setPinnedDate(date)
    setPlaying(false)
  }

  if (loading) {
    return (
      <div className="hv-root">
        <div className="hv-skeleton" />
      </div>
    )
  }
  if (error) return <div className="hv-root"><p className="hv-error">Could not load history: {error.message}</p></div>

  if (!league.dates.length) {
    return (
      <div className="hv-root">
        <div className="hv-card hv-empty-state">
          <h2>No history yet</h2>
          <p className="hv-muted">Rankings are snapshotted by the daily update job. Come back after its next run.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="hv-root" style={focus ? { '--team': focus.accent } : undefined}>
      <header className="hv-head">
        <div>
          <p className="hv-eyebrow">Rankings history</p>
          <h1>How the league's names stack up over time</h1>
        </div>
        <div className="hv-toolbar">
          <TeamPicker league={league} value={focusId} onChange={selectTeam} />
          <Segmented label="metric" options={METRICS} value={metric} onChange={setMetric} />
          <Segmented label="range" options={RANGES} value={range} onChange={r => { setRange(r); setPlaying(false) }} />
        </div>
      </header>

      <div className="hv-league">
        <section className="hv-card hv-league-main">
          <header className="hv-card-head">
            <div>
              <h3>{metric === 'rank' ? 'Rank race' : 'Score race'}</h3>
              <p className="hv-muted hv-small">
                Hover a line to see the team, click to explore it. Use <kbd>←</kbd> <kbd>→</kbd> to step through days.
              </p>
            </div>
            <div className="hv-player-controls">
              <span className="hv-active-date">{formatLongDay(activeDate)}</span>
              <button className="hv-play" onClick={togglePlay} aria-label={playing ? 'Pause replay' : 'Replay this period'}>
                {playing ? <Pause size={15} /> : <Play size={15} />}
                {playing ? 'Pause' : 'Replay'}
              </button>
            </div>
          </header>
          <LeagueChart
            league={league}
            dates={dates}
            metric={metric}
            focusId={focusId}
            hoverId={hoverId}
            onHover={setHoverId}
            pinnedDate={pinned}
            onPick={pick}
          />
        </section>
        <Standings league={league} date={activeDate} focusId={focusId} hoverId={hoverId} onHover={setHoverId} onSelect={selectTeam} />
      </div>

      {focus ? (
        <TeamStory
          key={focus.id}
          league={league}
          team={focus}
          dates={dates}
          pinnedDate={pinned}
          onPinDate={d => { setPinnedDate(d); setPlaying(false) }}
          onClose={() => setFocusId(null)}
        />
      ) : (
        <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <LeagueOverview league={league} dates={dates} onSelect={selectTeam} onHover={setHoverId} onPick={pick} />
        </motion.div>
      )}
    </div>
  )
}
